import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserProfile } from '@regatta-replay/types';

interface AuthState {
  token: string | null;
  user: UserProfile | null;
  isAuthenticated: boolean;
  login: (token: string, user: UserProfile) => void;
  logout: () => void;
  updateToken: (token: string) => void;
  validateSession: () => Promise<boolean>;
}

const API_BASE = import.meta.env.VITE_API_URL || '/api/v4';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      login: (token, user) => set({ token, user, isAuthenticated: true }),
      logout: () => set({ token: null, user: null, isAuthenticated: false }),
      updateToken: (token) => set({ token }),
      validateSession: async () => {
        const { token } = get();
        if (!token) return false;

        try {
          const res = await fetch(`${API_BASE}/auth/validate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });

          // Only an explicit rejection clears the session. A 5xx, a proxy
          // error or an offline moment means we do not KNOW the token is bad,
          // and signing someone out because the API blinked is worse than
          // letting the next real 401 do it.
          if (res.status === 401 || res.status === 403) {
            get().logout();
            return false;
          }
          if (!res.ok) return false;

          const data = await res.json();
          if (!data.valid) {
            get().logout();
            return false;
          }

          if (data.user) set({ user: data.user });
          if (data.refreshedToken) set({ token: data.refreshedToken });
          return true;
        } catch {
          return false;
        }
      },
    }),
    {
      name: 'regatta-replay-auth',
    }
  )
);
